import { useEffect, useRef } from 'react';
import { createStoreHooks, type FeatureDeps } from '../../core';
import type { Point2D } from '../../core/utils';
import { createCursorTrackingFeature } from './feature';

type UseCursorTrackingDeps = FeatureDeps<[typeof createCursorTrackingFeature]>;

const { useStoreApi } = createStoreHooks<UseCursorTrackingDeps>();

/**
 * Subscribe to cursor tracking changes without causing React rerenders.
 * Runs the handler on every cursor move (RAF-throttled).
 *
 * This is the equivalent of the old useCanvasEvent('cursor:content-move', handler)
 * but driven by Zustand state instead of events.
 */
export const useCursorTracking = (
  handler: (contentPoint: Point2D | null) => void
) => {
  const store = useStoreApi();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    let prevContentPoint: Point2D | null = null;

    const unsubscribe = store.subscribe(state => {
      const { contentPoint } = state.cursorTracking.state;
      if (contentPoint !== prevContentPoint) {
        prevContentPoint = contentPoint;
        handlerRef.current(contentPoint);
      }
    });

    return unsubscribe;
  }, [store]);
};
